/**
 * @fileOverview This file contains the axiosClient utility function and related constants for making HTTP requests.
 * @module axiosClient
 */

import axios from "axios";
import { encryptAndStoreToken, getDecryptedToken } from "./CryptoJS";
import LocalstorageService from "./localstorage-services";
// import Cookies from "universal-cookie";

axios.defaults.withCredentials = false;
let apiURL = "";

/**
 * API base URL from the build env (REACT_APP_BIGFOOT_API_URL, set per environment by
 * Jenkins from Bigfoot_Deploy/env/<env>/_common.env). Trailing "/" is removed because the
 * request paths here start with "/". Falls back to the old Dev API.
 * @type {string}
 */
// const ENV_API_URL = (
//   process.env.REACT_APP_BIGFOOT_API_URL || "https://api.dev.bigfootmail.com/api/v1"
// ).replace(/\/+$/, "");
let accountURL = "";

/**
 * Configuration options for the you only environment.
 * @type {Object}
 */
const bigFoot = {
  name: "bigFoot",
  account: "",
  api: "https://api.dev.bigfootmail.com/api/v1",
  // api: "https://apilb1.appcreationexperts.com/api/v1",
  live: "https://admin.dev.bigfootmail.com/",
  // live: "https://dev-admin.bigfootmail.com/",
};

/**
 * The domain of the application. If you're running the application locally, make sure to add the domain to the .env file.
 * @type {string}
 */
// eslint-disable-next-line no-undef
export const domain = process.env.REACT_APP_DOMAIN;

/**
 * Configuration options for the localhost environment.
 * @type {Object}
 */
const localhost = {
  account: "http://localhost:3000/",
  api: "https://api.dev.bigfootmail.com/api/v1",
  // api: "https://api.flyingspark.org/api/v1",
};

const path = window.location.href;
const live = !!path.match(bigFoot.live);

if (live) {
  accountURL = bigFoot.account;
  apiURL = bigFoot.api;
} else {
  accountURL = localhost.account;
  apiURL = localhost.api;
}

export const API_URL = apiURL;
export const ACCOUNT_URL = accountURL;

const handleUnauthorizedOrForbidden = async () => {
  let user = JSON.parse(localStorage.getItem("bigfoot-admin"));
  let tempObj = { refreshToken: getDecryptedToken(user?.refreshToken) };
  try {
    const response = await axios.post(
      `${API_URL}/admin/auth/refreshToken`,
      tempObj,
      {
        headers: {
          Authorization: `Bearer ${getDecryptedToken(user?.token)}`,
        },
      }
    );
    user = {
      ...user,
      token: encryptAndStoreToken(response.data.response.accessToken),
    };
    LocalstorageService.loginUser({ user: user });

    // Reload the page or retry the request after token update
    window.location.reload();
  } catch (error) {
    console.error("Failed to refresh token:", error);
    LocalstorageService.removeFromLocalStorage("bigfoot-admin");
    window.location = "/";
  }
};

/**
 * The axios client function for making HTTP requests to api.
 * @function axiosClient
 */
export function axiosClient() {
  // gets the email and refresh cookies
  const token = JSON.parse(localStorage.getItem("bigfoot-admin"))?.token;
  // const webToken = JSON.parse(localStorage.getItem("aboutcare-web"))?.token;

  const defaultOptions = {
    baseURL: apiURL,
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      // "Access-Control-Allow-Credentials": true,
      accept: "application/json",
    },
  };

  const instance = axios.create(defaultOptions);

  if (token) {
    instance.interceptors.request.use(
      (config) => {
        config.headers.Authorization = `Bearer ${getDecryptedToken(token)}`;
        return config;
      },
      (error) => Promise.reject(error)
    );

    instance.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${getDecryptedToken(token)}`;
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;

        if (status === 403) {
          window.location = "/unauthorized";
          // await handleUnauthorizedOrForbidden();
        }

        if (status === 401) {
          await handleUnauthorizedOrForbidden();
        }

        return Promise.reject(error);
      }
    );
  }

  return instance;
}

export default axiosClient;
